import type { UseMutateAsyncFunction } from '@tanstack/react-query'

import {
  type BookingSessionResponse,
  countBillablePassengers,
  type SubmitInsuranceRequest,
} from '@massilia/queries'
import { Button, cn, Label, RadioGroup, RadioGroupItem, Skeleton } from '@massilia/ui'
import { useNavigate } from '@tanstack/react-router'
import { AlertTriangle, Check, ChevronLeft, ExternalLink, Loader2 } from 'lucide-react'
import { useState } from 'react'

import { BookingSidebar } from '@/components/booking/booking-sidebar'
import { BookingStepActions } from '@/components/booking/booking-step-actions'
import { PromoCodeSlot } from '@/components/booking/promo-code-slot'
import { SessionInstallmentTimeline } from '@/components/booking/session-installment-timeline'
import { TripHeader } from '@/components/booking/trip-header'
import { usePromoCode } from '@/components/booking/use-promo-code'

import { formatPrice } from './utils'

interface AssurancesFormProps {
  sessionId: string
  isLoadingEstimate: boolean
  isSubmitting: boolean
  session?: BookingSessionResponse
  submitInsurance: UseMutateAsyncFunction<
    BookingSessionResponse,
    Error,
    SubmitInsuranceRequest,
    unknown
  >
}

export function AssurancesForm({
  sessionId,
  isLoadingEstimate,
  isSubmitting,
  session,
  submitInsurance,
}: AssurancesFormProps) {
  const navigate = useNavigate()

  // Pre-fill from session: if user already decided insurance (back-nav), restore their choice
  const [selectedInsurance, setSelectedInsurance] = useState<'with' | 'without'>(
    session?.insurance?.withInsurance ? 'with' : 'without'
  )

  // Insurance estimate — fetched on page load via useFetchInsuranceEstimate
  const insuranceEstimate = session?.insuranceEstimate
  const insuranceOption = insuranceEstimate?.option ?? null
  const insurancePriceCents = insuranceEstimate?.priceCents ?? 0
  const insurancePrice = insurancePriceCents / 100
  const hasInsurance = insuranceOption !== null && insurancePrice > 0
  const isInsuranceUnavailable = !isLoadingEstimate && !hasInsurance

  const [insuranceError, setInsuranceError] = useState(false)

  // Promo code: applied at a previous step but must remain visible/editable here.
  const {
    isVerifying: isPromoCodePending,
    promoCodeError,
    remove: removePromoCode,
    verify: verifyPromoCode,
  } = usePromoCode(sessionId)

  const promoCodeDiscount = session?.promoCode ? session.promoCode.discountCents / 100 : null
  const billablePassengerCount = countBillablePassengers(session?.flightDetails.passengersCount)

  const handleBack = () => {
    void navigate({
      search: { session: sessionId },
      to: '/informations',
    })
  }

  const handleNext = async () => {
    // Submit the insurance decision to the server, then navigate to checkout
    try {
      await submitInsurance({ withInsurance: selectedInsurance === 'with' })
      void navigate({
        search: { session: sessionId },
        to: '/paiement',
      })
    } catch {
      setInsuranceError(true)
    }
  }

  const sidebar = (
    <BookingSidebar
      insurancePriceLoading={isLoadingEstimate && selectedInsurance === 'with'}
      overrideInsurancePrice={selectedInsurance === 'with' ? insurancePrice : 0}
      promoCodeDiscount={promoCodeDiscount}
      promoCodeSlot=<PromoCodeSlot
        appliedCode={session?.promoCode?.code ?? null}
        discountEuros={promoCodeDiscount}
        error={promoCodeError}
        isVerifying={isPromoCodePending}
        onRemove={() => {
          void removePromoCode()
        }}
        onVerify={(code) => {
          void verifyPromoCode(code)
        }}
        passengerCount={billablePassengerCount}
      />
      session={session}
    />
  )

  // Prix affiché à côté du choix « avec assurance » : chargement, prix, ou tiret
  // si le prix n'est pas encore disponible.
  const withInsurancePriceLabel = hasInsurance ? `+ ${formatPrice(insurancePrice)} €` : '---'

  return (
    <div className="flex w-full flex-col gap-8 px-4 py-8 md:px-28 lg:flex-row lg:gap-12">
      {/* Right column — Sidebar (desktop) */}
      <div className="hidden w-full shrink-0 lg:order-last lg:block lg:w-96">{sidebar}</div>

      {/* Left column — Insurance choice */}
      <div className="flex flex-1 flex-col gap-9">
        {/* Récap voyage + échéancier (la date limite d'acompte est connue ici),
            en tête sur mobile comme sur desktop ; skeleton pendant le chargement */}
        {session ? <TripHeader session={session} /> : <SessionInstallmentTimeline loading />}

        {/* Insurance section title */}
        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-semibold text-gray-light-900">Voyagez en toute confiance</h2>
          <p className="text-sm text-gray-light-900">
            L&apos;inattendu peut arriver. Ajoutez une couverture à votre voyage pour les imprévus
            et partez sereinement, quelles que soient les circonstances.
          </p>
        </div>

        {/* Insurance error */}
        {insuranceError && (
          <div className="flex items-start gap-3 rounded-xl border border-warning/40 bg-warning-subtle p-4">
            <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warning-foreground" />
            <div className="flex flex-col gap-1">
              <p className="text-sm font-semibold text-gray-light-900">
                Service d&apos;assurance indisponible
              </p>
              <p className="text-sm text-gray-light-700">
                Nous n&apos;avons pas pu récupérer les offres d&apos;assurance. Vous pouvez
                continuer sans assurance ou réessayer plus tard.
              </p>
            </div>
          </div>
        )}

        {/* Insurance radio options */}
        <div className="flex w-full flex-col gap-4">
          <RadioGroup
            className="flex flex-col gap-0"
            onValueChange={(value) => {
              if (value === 'with' || value === 'without') {
                setSelectedInsurance(value)
              }
            }}
            value={selectedInsurance}
          >
            {/* Without insurance */}
            <div
              className={cn(
                'border-gray-light-300 cursor-pointer rounded-t-2xl border-t border-r border-l transition-all',
                selectedInsurance === 'without'
                  ? 'border-brand-600 bg-brand-50 border-b'
                  : 'hover:bg-gray-light-50'
              )}
              onClick={() => setSelectedInsurance('without')}
            >
              <div className="flex items-start gap-2 p-4">
                <RadioGroupItem className="mt-0.5" id="without" value="without" />
                <div className="flex-1">
                  <Label className="flex-1 cursor-pointer text-sm font-semibold" htmlFor="without">
                    Sans assurance
                  </Label>
                </div>
                <div className="text-sm font-semibold text-gray-light-900">0,00 &euro;</div>
              </div>
              <div
                className={cn(
                  'overflow-hidden transition-all duration-300 ease-in-out',
                  selectedInsurance === 'without' ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'
                )}
              >
                <div className="px-4 pb-4">
                  <p className="text-sm text-gray-light-700">
                    Votre vol n&apos;est pas assuré au-delà des conditions de
                    modification/annulation
                  </p>
                </div>
              </div>
            </div>

            {/* With insurance */}
            <div
              className={cn(
                'border-gray-light-300 cursor-pointer rounded-b-2xl border-r border-b border-l transition-all',
                selectedInsurance === 'with'
                  ? 'border-brand-600 bg-brand-50 border-t'
                  : 'hover:bg-gray-light-50',
                (insuranceError || isInsuranceUnavailable) && 'pointer-events-none opacity-50'
              )}
              onClick={() => {
                if (!insuranceError && hasInsurance && !isLoadingEstimate)
                  setSelectedInsurance('with')
              }}
            >
              <div className="flex items-start gap-2 p-4">
                <RadioGroupItem
                  className="mt-0.5"
                  disabled={insuranceError || isInsuranceUnavailable || isLoadingEstimate}
                  id="with"
                  value="with"
                />
                <div className="flex-1">
                  <Label className="flex-1 cursor-pointer text-sm font-semibold" htmlFor="with">
                    Avec assurance multirisques
                  </Label>
                </div>
                <div className="text-sm font-semibold text-gray-light-900">
                  {isLoadingEstimate || isSubmitting ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    withInsurancePriceLabel
                  )}
                </div>
              </div>
              <div
                className={cn(
                  'overflow-hidden transition-all duration-300 ease-in-out',
                  selectedInsurance === 'with' ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                )}
              >
                {insuranceOption && (
                  <div className="flex w-full flex-col gap-3 px-4 pb-4">
                    {/* Formula name */}
                    <div className="flex items-center gap-2">
                      <Check className="size-4 shrink-0 text-success-600" />
                      <p className="text-sm text-gray-light-900">{insuranceOption.nomFormule}</p>
                    </div>

                    {/* Extensions */}
                    {insuranceOption.extensions.map((ext) => (
                      <div className="flex items-center gap-2" key={ext.id}>
                        <Check className="size-4 shrink-0 text-success-600" />
                        <p className="text-sm text-gray-light-900">{ext.label}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </RadioGroup>

          <div className="mt-2 flex flex-col gap-2">
            <p className="text-sm font-semibold text-gray-light-900">
              Qu&apos;est-ce qui est couvert par l&apos;assurance ?
            </p>
            {isLoadingEstimate ? (
              <div className="flex flex-col gap-2">
                <Skeleton className="h-4 w-64" />
                <Skeleton className="h-4 w-60" />
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {insuranceOption?.dinLink && (
                  <a
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-600 underline hover:text-brand-700"
                    href={insuranceOption.dinLink}
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    <ExternalLink className="size-3 shrink-0" />
                    Document d&apos;information
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile : détail du tarif consultable en fin d'étape */}
        <div className="lg:hidden">{sidebar}</div>

        {/* Navigation buttons */}
        <BookingStepActions>
          <Button className="gap-2" onClick={handleBack} type="button" variant="ghost">
            <ChevronLeft className="size-4 text-brand-700" />
            <span className="text-sm font-medium text-brand-700">Étape précédente</span>
          </Button>
          <Button
            className="min-w-44 gap-2 bg-brand-600 px-6 hover:bg-brand-700"
            disabled={isSubmitting}
            onClick={() => {
              void handleNext()
            }}
            type="button"
          >
            {isSubmitting ? (
              <Loader2 className="size-4 animate-spin text-white" />
            ) : (
              <span className="text-sm font-medium text-white">Continuer</span>
            )}
          </Button>
        </BookingStepActions>
      </div>
    </div>
  )
}
